import { ApolloClient } from 'apollo-client';
import { SEMANTIC_SEARCH } from '../aiRelatedContent/graphql';
import { AI_ASSISTANT_MESSAGE } from 'graphql/aiSearch';
import { articleEditPath, globalIdToOid } from 'utils/globalId';
import { CreatedContent, Message, MessageAction, PageContext, PageType, SearchResultItem } from './types';

const PAGE_SIZE = 5;
// aiSemanticSearch caps limit at 50 server-side
const MAX_LIMIT = 50;

type AIResponse = Omit<Message, 'id' | 'role' | 'timestamp'>;

/**
 * True when the message reads as a request to find content, which is the one
 * shape the drawer can still answer on its own when the assistant is off.
 */
export function isRelatedContentQuery(message: string): boolean {
  const lower = message.toLowerCase();
  const patterns = [
    'related', 'similar', 'find articles', 'find content', 'search for',
    'suggest content', 'recommend', 'discover', 'what articles',
    'show me articles', 'recent articles', 'help me find', 'content about',
    'articles about', 'topics like', 'related to this'
  ];
  return patterns.some(p => lower.includes(p));
}

/** Strips the conversational lead-in so what's left is the search itself. */
export function extractSearchTerms(message: string): string {
  const lower = message.toLowerCase();
  const prefixes = [
    'find articles about', 'find content about', 'search for',
    'find articles on', 'find content on', 'show me articles about',
    'articles about', 'content about', 'related to',
    'find related', 'suggest content about', 'recommend articles about'
  ];
  let terms = lower;
  for (const prefix of prefixes) {
    if (terms.includes(prefix)) {
      terms = terms.substring(terms.indexOf(prefix) + prefix.length).trim();
      break;
    }
  }
  return terms;
}

const PAGE_TYPE_LABELS: Partial<Record<PageType, string>> = {
  'dashboard': 'Dashboard',
  'article-list': 'Articles',
  'article-edit': 'Editing Article',
  'event-list': 'Events',
  'event-edit': 'Editing Event',
  'user-list': 'Users',
  'user-edit': 'User Profile',
  'media': 'Media Library',
  'analytics': 'Analytics',
  'taxonomy': 'Taxonomy',
  'workflow': 'Workflow',
  'settings': 'Settings',
};

/** The breadcrumb the drawer shows for whatever the user is looking at. */
export function getContextDescription(pageContext: PageContext | null): string {
  if (!pageContext) return '';

  const { pageType, entity, section } = pageContext;
  let description = '';

  if (section) {
    description += `${section.charAt(0).toUpperCase() + section.slice(1)} > `;
  }

  description += PAGE_TYPE_LABELS[pageType] || pageType;

  if (entity?.title) {
    description += `: ${entity.title}`;
  }

  return description;
}

// Remembers the last real search so "Load More" can grow the limit.
let lastSearch: { terms: string; limit: number } | null = null;

// e.g. "article" -> "Article" (chat badge styling keys on capitalized types)
const toContentType = (raw: string | null): SearchResultItem['contentType'] => {
  if (!raw) return 'Article';
  const pascal = raw
    .split(/[_\s]+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join('');
  return pascal as SearchResultItem['contentType'];
};

const toChatResult = (r: any): SearchResultItem => {
  const contentType = toContentType(r.contentType);
  const canonicalUrl = r.canonicalUrl || '';
  const oid = r.oid || globalIdToOid(r.id);
  return {
    id: String(r.id || ''),
    // Primary link opens the item in the admin when we know the route;
    // canonicalUrl (the public site path) is used for link insertion.
    url: contentType === 'Article' && oid ? articleEditPath(oid) : canonicalUrl,
    canonicalUrl,
    imageUrl: r.imageUrl || undefined,
    title: r.title || 'Untitled',
    publishedAt: r.postAt || '',
    authors: (r.authorNames || []).filter(Boolean),
    taxonomies: (r.taxonomyNames || []).filter(Boolean),
    keywords: (r.keywordNames || []).filter(Boolean),
    score: r.score ?? 0,
    matchReason: r.matchReason || '',
    source: r.source === 'keyword' || r.source === 'both' ? r.source : 'semantic',
    contentType,
  };
};

/**
 * A created item from the API. Its url falls back to the edit route so a
 * created article is never shown without somewhere to open it.
 */
export const toCreated = (c: any): CreatedContent | null => {
  // Without an oid, take it from the url so history can still name the item;
  // a url we can't read an id from is still somewhere to open it.
  const oid = globalIdToOid(c?.oid) || (String(c?.url || '').match(/\/articles\/(-?\d+)(?![\w-])/) || [])[1] || '';
  if (!oid && !c?.url) return null;
  return {
    kind: c.kind === 'brief' ? 'brief' : 'article',
    oid,
    title: c.title || 'Untitled',
    url: c.url || articleEditPath(oid),
    published: !!c.published,
    sources: (c.sources || []).filter((s: any) => s?.url).map((s: any) => ({ text: s.text, url: s.url })),
  };
};

/**
 * One turn of history for the assistant. Created content rides along with
 * its id: the API is stateless, and without it a follow-up like "tighten that
 * brief" has nothing to point at.
 */
export const toHistoryTurn = (m: Pick<Message, 'role' | 'content' | 'created' | 'selectedArticleIds'>) => {
  const notes = (m.created || [])
    .map((c) => `[Created ${c.kind} "${c.title}"${c.oid ? ` (article_id ${c.oid})` : ''}]`);
  const selected = selectionNote(m.selectedArticleIds);
  if (selected) notes.push(selected);
  return { role: m.role, content: notes.length ? `${m.content}\n${notes.join('\n')}` : m.content };
};

/**
 * The rows checked on the article list, by id only (their text is sent just
 * with the Create brief request). Enough for "brief these on pricing" or a
 * follow-up answer to name them as create_brief's sources.
 */
export const selectionNote = (ids?: string[]) => {
  const oids = (ids || []).map(globalIdToOid).filter(Boolean);
  return oids.length ? `[Rows checked on the article list: article_ids ${oids.join(', ')}]` : '';
};

/** True when the assistant is unavailable rather than merely unhappy. */
const isUnavailable = (errors: string[] = []) =>
  errors.some((e) => /not enabled|not configured|model access|denied/i.test(e));

async function runSemanticSearch(
  client: ApolloClient<any>,
  terms: string,
  limit: number,
): Promise<AIResponse> {
  const { data } = await client.query({
    query: SEMANTIC_SEARCH,
    variables: { query: terms, limit, contentTypes: ['article'] },
    fetchPolicy: 'network-only',
  });

  const response = data?.aiSemanticSearch;
  const results: SearchResultItem[] = (response?.results || [])
    .filter(Boolean)
    .map(toChatResult);

  lastSearch = { terms, limit };

  if (results.length === 0) {
    return {
      content: `I couldn't find any content matching "${terms}". Try broadening your search or using different keywords.`,
      links: [],
      actions: [],
      searchResults: [],
    };
  }

  const actions: MessageAction[] = [];
  if (results.length >= limit && limit < MAX_LIMIT) {
    actions.push({ id: 'load-more', label: 'Load More Results', type: 'custom', payload: { action: 'load-more' } });
  }

  return {
    content: `I found ${results.length} content item${results.length === 1 ? '' : 's'} for "${terms}", ranked by a combination of semantic similarity and keyword relevance. You can insert links or add items as related content.`,
    links: [],
    actions,
    searchResults: results,
  };
}

interface AskOptions {
  /** Articles the request is about (the Create brief button's selection) */
  articleIds?: string[];
  /** Rows checked on the list; named by id when articleIds isn't sent */
  selectedArticleIds?: string[];
}

/**
 * Ask the assistant. The model chooses its own tools (search, read, create,
 * update, tag, relate, SEO) and the API executes them as this user.
 */
async function askAssistant(
  client: ApolloClient<any>,
  message: string,
  pageContext: PageContext | null | undefined,
  history: { role: string; content: string }[],
  options: AskOptions,
): Promise<AIResponse | null> {
  const articleId = pageContext?.pageType === 'article-edit' ? pageContext?.entity?.id : undefined;
  // The API puts these articles' text in the prompt (and caps how many), so
  // only a request that asks for them sends them. Otherwise the checked rows
  // ride along by id, so a typed "brief these" can still name them.
  const { articleIds } = options;
  const note = articleIds?.length ? '' : selectionNote(options.selectedArticleIds);

  const { data } = await client.mutate({
    mutation: AI_ASSISTANT_MESSAGE,
    variables: {
      message: note ? `${message}\n\n${note}` : message,
      articleId: articleId ? String(articleId) : undefined,
      articleIds: articleIds?.length ? articleIds.map(String) : undefined,
      history: history.slice(-10).map((t) => ({ role: t.role, content: t.content })),
    },
  });

  const payload = data?.aiAssistantMessage;
  if (!payload) return null;

  if (!payload.success) {
    // Unavailable (not enabled / no model access) falls back to plain search;
    // a genuine failure is reported as-is.
    if (isUnavailable(payload.errors || [])) return null;
    return {
      content: (payload.errors || ['The assistant could not complete that.']).join(' '),
      links: [],
      actions: [],
    };
  }

  const created = (payload.created || []).map(toCreated).filter(Boolean) as CreatedContent[];
  // A created item's link is its card's Open button; don't list it twice.
  const createdUrls = new Set(created.map((c) => c.url));

  return {
    content: payload.content || '',
    reasoning: (payload.reasoning || []).filter(Boolean),
    toolsUsed: (payload.toolsUsed || []).filter(Boolean),
    created,
    links: (payload.links || [])
      .filter((l: any) => l && !createdUrls.has(l.url))
      .map((l: any) => ({ text: l.text, url: l.url })),
    actions: [],
    searchResults: (payload.searchResults || []).filter(Boolean).map(toChatResult),
  };
}

export async function generateAIResponse(
  userMessage: string,
  pageContext: PageContext | null | undefined,
  client: ApolloClient<any>,
  history: { role: string; content: string }[] = [],
  options: AskOptions = {},
): Promise<AIResponse> {
  try {
    const assistant = await askAssistant(client, userMessage, pageContext, history, options);
    if (assistant) return assistant;
  } catch (e: any) {
    // Fall through to search so the drawer stays useful if the model errors.
    // eslint-disable-next-line no-console
    console.warn('AI assistant unavailable, falling back to search:', e?.message);
  }

  // Fallback: direct semantic search for search-shaped questions.
  if (isRelatedContentQuery(userMessage)) {
    const terms = extractSearchTerms(userMessage);
    try {
      return await runSemanticSearch(client, terms || userMessage, PAGE_SIZE);
    } catch (e: any) {
      return { content: `Search failed: ${e?.message || 'unknown error'}.`, links: [], actions: [] };
    }
  }

  return {
    content: `The AI assistant isn't available right now, so I can only search content — try "find articles about <topic>".`,
    links: [],
    actions: [],
  };
}

/**
 * Runs a chat action. Routing is entirely off the action's payload -- the
 * assistant is told what to do in natural language, so the only action the
 * client resolves itself is paging an earlier search.
 */
export async function executeAction(
  payload: any,
  client: ApolloClient<any>,
): Promise<string | AIResponse> {
  if (payload?.action === 'load-more' && lastSearch) {
    const limit = Math.min(lastSearch.limit + PAGE_SIZE, MAX_LIMIT);
    try {
      return await runSemanticSearch(client, lastSearch.terms, limit);
    } catch (e: any) {
      return `Search failed: ${e?.message || 'unknown error'}.`;
    }
  }

  // Everything else is expressed to the assistant in natural language.
  if (payload?.prompt) {
    return await generateAIResponse(payload.prompt, payload.pageContext, client);
  }

  return `That action isn't wired up yet.`;
}
